import { GlobalNav } from '@inmotionnow/momentum-components-react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useAuth } from '@/auth/AuthContext'
import { useGetCurrentUserQuery } from '@/features/users/userApi'

export const NavPublic = (): JSX.Element => {
  const { t } = useTranslation()
  const { isStaff, logout } = useAuth()
  const { data: currentUser } = useGetCurrentUserQuery(undefined, {
    skip: isStaff,
  })
  const location = useLocation()
  const userId = currentUser?.id
  const accountId = currentUser?.account.id
  const isAccountAdmin = currentUser?.user_type === 'admin'

  const roleLogo = isStaff
    ? '/logo-staff.svg'
    : isAccountAdmin
      ? '/logo-account-admin.svg'
      : '/logo-account-team-member.svg'

  const roleTitle = isStaff
    ? 'LYTHO STAFF'
    : isAccountAdmin
      ? 'ACCOUNT ADMIN'
      : 'ACCOUNT TEAM MEMBER'

  return (
    <nav className='mds-c-navigation' data-testid='app-nav'>
      <div className='mds-c-navigation__header'>
        <div className='mds-c-navigation__logo'>
          <img alt={`${t('NAV_TITLE')} Logo`} src='/logo.png' />
          <span className='mds-h-screenreader-text'>{t('NAV_TITLE')}</span>
        </div>
        <img src={roleLogo} alt={roleTitle} />
      </div>
      <div className='mds-c-navigation__main'>
        <GlobalNav>
          {userId !== undefined && (
            <GlobalNav.Item>
              <GlobalNav.Link
                to={`/user/details/${userId}`}
                icon='user'
                label={t('NAV_MY_PROFILE')}
                isActive={location.pathname.startsWith('/user/details')}
              />
            </GlobalNav.Item>
          )}
          {isAccountAdmin && accountId !== undefined && (
            <GlobalNav.Item>
              <GlobalNav.Link
                to={`/account/${accountId}/users`}
                icon='team'
                label={t('NAV_ACCOUNT_MEMBERS')}
                isActive={location.pathname.startsWith(
                  `/account/${accountId}/users`,
                )}
              />
            </GlobalNav.Item>
          )}
          {/* The way back to an import once the tab that started it is closed. Admins
              only, like the import itself — a team member would get a 403. Stays active
              on a run's own page, which sits under the same path. */}
          {isAccountAdmin && accountId !== undefined && (
            <GlobalNav.Item>
              <GlobalNav.Link
                to={`/account/${accountId}/imports`}
                icon='upload'
                label={t('NAV_BULK_IMPORTS')}
                isActive={location.pathname.startsWith(
                  `/account/${accountId}/imports`,
                )}
              />
            </GlobalNav.Item>
          )}
          {isStaff && (
            <GlobalNav.Item>
              <GlobalNav.Link
                to='/admin/accounts'
                icon='briefcase'
                label={t('NAV_ACCOUNTS')}
                isActive={location.pathname.startsWith('/admin/accounts')}
              />
            </GlobalNav.Item>
          )}
          {isStaff && (
            <GlobalNav.Item>
              <GlobalNav.Link
                to='/admin/account/create'
                icon='briefcase'
                label={t('NAV_CREATE_ACCOUNT')}
                isActive={location.pathname === '/admin/account/create'}
              />
            </GlobalNav.Item>
          )}
          {!isStaff && isAccountAdmin && accountId !== undefined && (
            <GlobalNav.Item>
              <GlobalNav.Link
                to={`/user/create?accountId=${accountId}`}
                icon='user-add'
                label={t('NAV_CREATE_USER')}
                isActive={location.pathname === '/user/create'}
              />
            </GlobalNav.Item>
          )}
          <GlobalNav.Item>
            <GlobalNav.Link
              icon='power'
              label={t('NAV_LOGOUT')}
              onClick={logout}
            />
          </GlobalNav.Item>
        </GlobalNav>
      </div>
      {currentUser && (
        <div className='mds-c-navigation__footer'>
          <span>{currentUser.display_name}</span>
        </div>
      )}
    </nav>
  )
}
